import EventEmitter from 'eventemitter3';
import {
  SOCKET_STATE,
  SOCKET_QUEUE_INTERVAL,
  SOCKET_RECONNECT_INTERVAL,
  SOCKET_IDLE_WATCH_INTERVAL,
  SOCKET_EVENTS,
} from '../constants';
import { noop } from './index';

class SocketCore {
  constructor(url, sendMessageWhenIdle, ctrl) {
    if (SocketCore.__instance && SocketCore.__instance[url]) {
      return SocketCore.__instance[url];
    }
    this.url = url;
    this.ctrl = ctrl;
    SocketCore.__instance = SocketCore.__instance || {};
    SocketCore.__instance[this.url] = this;
    this.__callbacks = {
      open: [],
      close: [],
      error: [],
      message: [],
    };
    this.__connect();
    this.__sendIdleMessage();
    this.sendMessageWhenIdle = sendMessageWhenIdle;
  }

  register(event, callback) {
    if ((event === 'open' && this.readyState === 1) || (event === 'close' && this.readyState === 3)) {
      callback();
    }
    this.__callbacks[event].push(callback);
  }

  get readyState() {
    if (this.__ws) {
      return this.__ws.readyState;
    }
    return SOCKET_STATE.NULL;
  }

  setBinaryType(value) {
    if (value instanceof Blob) {
      this.__ws.binaryType = 'blob';
      // eslint-disable-next-line no-undef
    } else if (value instanceof ArrayBuffer) {
      this.__ws.binaryType = 'arraybuffer';
    }
  }

  send(payload) {
    if (this.__ws) {
      this.__ws.send(payload);
    }
  }

  UrlAfterCheckingForSid(url) {
    var localStoredSid = null;
    if (url.includes('&sid=')) {
      return url;
    } else {
      try {
        const appConfig = this.ctrl.appConfig;
        const { eventId, jwtData } = appConfig;
        localStoredSid = localStorage.getItem(`act_${eventId}_${jwtData.userID}`);
      } catch (err) {
        localStoredSid = null;
      }
      if (localStoredSid) {
        url += `&sid=${localStoredSid}`;
      }
      return url;
    }
  }

  __connect() {
    if (!this.__ws) {
      const socketUrl = this.UrlAfterCheckingForSid(this.url);
      const socket = new WebSocket(socketUrl);
      socket.onclose = this.__onSocketClose.bind(this);
      socket.onerror = this.__onSocketError.bind(this);
      socket.onmessage = this.__onSocketMessage.bind(this);
      socket.onopen = this.__onSocketOpen.bind(this);
      this.__ws = socket;
      this.__reconnectWS = true;
    }
  }

  close() {
    if (this.__ws) {
      this.__ws.close();
      if (!this.closedDueToInactivity) {
        this.__reconnectWS = false;
        SocketCore.__instance[this.url] = null;
      }
    }
  }

  __onSocketOpen(e) {
    this.__callbacks.open.forEach(function (callback = noop) {
      callback(this);
    });
  }
  __onSocketError(e) {
    this.__callbacks.error.forEach(function (callback = noop) {
      callback(e);
    });
  }

  __onSocketMessage(e) {
    this.__callbacks.message.forEach(function (callback = noop) {
      callback(e);
    });
  }

  __onSocketClose(e) {
    const { code } = e;
    this.__callbacks.close.forEach(function (callback = noop) {
      callback(e);
    });
    if (code !== 1000 && code !== 1011 && this.__reconnectWS) {
      clearTimeout(this.__reconnectInterval);
      this.__reconnectInterval = window.setTimeout(() => {
        delete this.__ws;
        this.__connect();
      }, SOCKET_RECONNECT_INTERVAL);
    }
  }

  __sendIdleMessage() {
    clearTimeout(this.__idleMessageTimer);
    this.__idleMessageTimer = window.setTimeout(() => {
      if (this.sendMessageWhenIdle && this.readyState === SOCKET_STATE.OPEN) {
        this.send(':900');
      }
      this.__sendIdleMessage();
    }, SOCKET_IDLE_WATCH_INTERVAL);
  }
}

export default class Socket extends EventEmitter {
  constructor(url, sendMessageWhenIdle = false, ctrl) {
    super();
    this.__messageQueue = [];
    this.url = url;
    this.sendMessageWhenIdle = sendMessageWhenIdle;
    if (ctrl) {
      this.ctrl = ctrl;
      this.session = ctrl.session;
    }
    this.__connect();
    this.IdleTimeOut = 30000; //idle time out
    // this.__sendIdleMessage();
  }

  __connect() {
    setTimeout(() => {
      this.__ws = new SocketCore(this.url, this.sendMessageWhenIdle, this.ctrl);
      this.__ws.register('open', this.__onSocketOpen.bind(this));
      this.__ws.register('close', this.__onSocketClose.bind(this));
      this.__ws.register('error', this.__onSocketError.bind(this));
      this.__ws.register('message', this.__onSocketMessage.bind(this));
    }, 50);
  }

  get wsReference() {
    if (this.__ws && this.__ws.__ws) {
      return this.__ws.__ws;
    }
    return null;
  }

  get readyState() {
    if (this.__ws) {
      return this.__ws.readyState;
    }
    return SOCKET_STATE.NULL;
  }

  addEventListener() {
    super.on.apply(this, Array.prototype.slice.call(arguments));
  }

  __onSocketClose(e) {
    clearTimeout(this.socketIdleDestroyTimer);
    this.emit(SOCKET_EVENTS.CLOSE, e);
  }

  __onSocketOpen() {
    clearTimeout(this.socketIdleDestroyTimer);
    this.emit(SOCKET_EVENTS.OPEN, this);
    this.__startQueueWatch();
  }
  __onSocketError(e) {
    this.emit(SOCKET_EVENTS.ERROR, e);
  }
  __onSocketMessage(e) {
    let { data } = e;
    this.__handleSocketIdle();
    this.emit(SOCKET_EVENTS.MESSAGE, data);
  }
  __clearQueueMessage() {
    while (this.__messageQueue.length !== 0) {
      if (this.readyState === SOCKET_STATE.OPEN) {
        const message = this.__messageQueue[0];
        const messageString = JSON.stringify(message);
        try {
          this.__ws.send(messageString);
          this.__messageQueue.shift();
        } catch (e) {
          console.log('send', e);
          break;
        }
      } else {
        break;
      }
    }
  }
  __startQueueWatch() {
    clearTimeout(this.__interval);
    this.__interval = window.setTimeout(() => {
      if (this.__ws && this.readyState === SOCKET_STATE.OPEN) {
        this.__clearQueueMessage();
      }
      this.__startQueueWatch();
    }, SOCKET_QUEUE_INTERVAL);
  }

  __handleSocketIdle() {
    clearTimeout(this.socketIdleDestroyTimer);
    if (this.__ws) this.__ws.closedDueToInactivity = false;
    this.socketIdleDestroyTimer = setTimeout(() => {
      if (this.__ws) this.__ws.closedDueToInactivity = true;
      this.disconnect();
      this.emit(SOCKET_EVENTS.CLOSE);
    }, this.IdleTimeOut);
  }

  disconnect() {
    this.__ws && this.__ws.close();
  }

  addInQueue(payload) {
    this.__messageQueue.push(payload);
  }

  sendJson(payload) {
    const data = JSON.stringify(payload);
    this.send(data);
  }

  sendBinary(payload) {
    if (this.__ws) {
      this.__ws.setBinaryType(payload);
      this.send(payload);
    }
  }

  send(payload) {
    if (this.__ws) {
      if (this.readyState === SOCKET_STATE.OPEN) {
        try {
          this.__ws.send(payload);
        } catch (e) {
          this.addInQueue(payload);
        }
      } else {
        this.addInQueue(payload);
      }
    }
  }
}
